import React, { useImperativeHandle, useRef } from 'react';

import { Minus, Plus } from 'lucide-react';
import { Button } from './button';
import { Input } from './input';
import { Surface } from './surface';
import { cn } from './utils';

import type { ButtonProps } from './button';

export type NumberInputProps = {
  invalid?: boolean;
  decreaseButtonProps?: Partial<ButtonProps>;
  increaseButtonProps?: Partial<ButtonProps>;
  suffix?: React.ReactNode;
  onDecreaseClick: () => void;
  onIncreaseClick: () => void;
} & Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type'>;

const NumberInput = React.forwardRef<HTMLInputElement, NumberInputProps>((props, ref) => {
  const {
    className,
    onFocus,
    onBlur,
    invalid,
    decreaseButtonProps,
    increaseButtonProps,
    suffix,
    onDecreaseClick,
    onIncreaseClick,
    ...rest
  } = props;
  const [focused, setFocused] = React.useState(false);

  function ringClassName(): string {
    if (invalid) return 'ring-1 ring-support-error';
    if (focused) return 'ring-1 ring-focus';
    return '';
  }

  const chWidth = Math.max(String(props.value).length, String(rest.placeholder ?? '').length, 1);

  const inputRef = useRef<HTMLInputElement>(null);

  useImperativeHandle(ref, () => inputRef.current!, []);

  const handleContainerClick = (e: React.MouseEvent) => {
    if ((e.target === e.currentTarget || e.currentTarget.contains(e.target as Node)) && !(e.target as HTMLElement).closest('button')) {
      inputRef.current?.focus();
    }
  };

  return (
    <Surface
      variant="panel"
      showBorder={true}
      radius="small"
      className={cn('w-full py-1 px-1', ringClassName())}
      style={{ backgroundColor: 'rgba(250, 250, 249, 0.1)' }}
      onClick={handleContainerClick}
    >
      <div className="flex w-full max-w-full flex-row items-center justify-between gap-2">
        <div className="flex">
          <Button type="button" variant="secondary" size="icon" onClick={onDecreaseClick} {...decreaseButtonProps}>
            <Minus className="h-4 w-4" strokeWidth={2.5} />
          </Button>
        </div>

        <div className={cn('inline-flex items-center flex-1 min-w-0 gap-[3px]', suffix ? 'justify-center' : 'justify-start')}>
          <Input
            {...rest}
            type="text"
            inputMode="decimal"
            className={cn(
              'placeholder:text-text-placeholder text-sm text-center min-w-0 border-0 bg-transparent px-0 focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-none',
              suffix ? '' : 'flex-1',
              className,
            )}
            ref={inputRef}
            style={suffix ? { width: `${Math.min(chWidth, 20)}ch` } : undefined}
            autoComplete="off"
            data-form-type="other"
            onFocus={e => {
              setFocused(true);
              onFocus?.(e);
            }}
            onBlur={e => {
              setFocused(false);
              onBlur?.(e);
            }}
          />
          {suffix && <span className="select-none text-sm text-muted-foreground">{suffix}</span>}
        </div>

        <div className="flex">
          <Button type="button" variant="secondary" size="icon" onClick={onIncreaseClick} {...increaseButtonProps}>
            <Plus className="h-4 w-4" strokeWidth={2.5} />
          </Button>
        </div>
      </div>
    </Surface>
  );
});

NumberInput.displayName = 'NumberInput';

export { NumberInput };
